import { useCallback, useEffect, useState } from 'react';
import { addCaseNote, EngineError, listCaseNotes, type CaseNote } from '../../api/engineDashboard';
import { formatDateTime } from '../../lib/labels';

interface Props {
  token: string;
  caseId: string;
  canMutate: boolean;
  onUnauthorized: () => void;
}

/**
 * Case notes: append-only internal mediator work product. Corrections are
 * new notes — nothing edits or deletes, matching the audit-trail ethos.
 */
export function NotesPanel({ token, caseId, canMutate, onUnauthorized }: Props) {
  const [notes, setNotes] = useState<CaseNote[] | null>(null);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    listCaseNotes(token, caseId)
      .then((res) => setNotes(res.notes))
      .catch((err: unknown) => {
        if (err instanceof EngineError && err.status === 401) return onUnauthorized();
        setNotes([]);
      });
  }, [token, caseId, onUnauthorized]);

  useEffect(load, [load]);

  const save = async () => {
    const body = draft.trim();
    if (!body) return;
    setSaving(true);
    setError(null);
    try {
      const note = await addCaseNote(token, caseId, body);
      setNotes((prev) => [note, ...(prev ?? [])]);
      setDraft('');
    } catch (err) {
      if (err instanceof EngineError && err.status === 401) return onUnauthorized();
      setError(err instanceof EngineError ? err.message : 'Could not save the note.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="card p-4">
      <div className="section-label">Notes</div>

      {canMutate && (
        <div className="mt-2">
          <textarea
            className="w-full rounded-md border border-surface-border p-2 text-sm"
            rows={3}
            placeholder="Add a note for this case… (internal — never visible to parties or referral sources)"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
          />
          <button
            type="button"
            className="btn-secondary mt-1.5 w-full"
            disabled={saving || !draft.trim()}
            onClick={() => void save()}
          >
            {saving ? 'Saving…' : 'Add note'}
          </button>
        </div>
      )}
      {error && <div className="mt-1.5 text-xs text-red-600">{error}</div>}

      {notes === null ? (
        <p className="mt-2 text-xs text-gray-400">Loading…</p>
      ) : notes.length === 0 ? (
        <p className="mt-2 text-xs text-gray-500">No notes yet.</p>
      ) : (
        <ul className="mt-2 space-y-2">
          {notes.map((n) => (
            <li key={n.id} className="rounded-md border border-surface-border p-2 text-sm">
              <div className="whitespace-pre-wrap">{n.body}</div>
              <div className="mt-1 text-xs text-gray-400">
                {n.author_name} · {formatDateTime(n.created_at)}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
